import { Injectable } from '@angular/core';
import {
    HttpEvent,
    HttpInterceptor,
    HttpHandler,
    HttpRequest,
    HttpResponse,
    HttpErrorResponse
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { NetworkService } from '../services';
import { CacheService, ONE_HOUR, ONE_DAY, ONE_WEEK } from './cache.service';
import { Capacitor } from '@capacitor/core';

// Cache patterns with differentiated TTLs based on data type
const cachePatterns: { pattern: RegExp; ttl: number }[] = [
    // ===== Real-time/User Data (1 HOUR) =====
    // Active tickets change frequently, need fresh data
    { pattern: /^https?:\/\/.*\/core\/customer\/v3\/region\/.*\/ticket\/active$/, ttl: ONE_HOUR },
    { pattern: /^https?:\/\/.*\/core\/customer\/v3\/region\/.*\/ticket\/active\?.*$/, ttl: ONE_HOUR },
    { pattern: /^https?:\/\/wmb-public-api-.*\.ridango.cloud\/v1\/ticket((\?.*)|(\/.*))$/, ttl: ONE_HOUR },

    // User favorites and preferences
    { pattern: /^https?:\/\/.*\/v1\/favorite\/ticket$/, ttl: ONE_HOUR },
    { pattern: /^https?:\/\/.*\/v1\/favorite\/schedule$/, ttl: ONE_HOUR },

    // Device-specific data
    { pattern: /^https?:\/\/.*\/core\/customer\/v2\/region\/.*\/bob\/.*\/device\/kdk*$/, ttl: ONE_HOUR },

    // Real-time schedule data
    { pattern: /^https?:\/\/.*\/v1\/stops\/.*\/stoptimes\?.*$/, ttl: ONE_HOUR },

    // ===== Product/Region Data (1 DAY) =====
    // Products and categories change occasionally
    { pattern: /^https?:\/\/.*\/v2\/.*\/categories\/product\/detail$/, ttl: ONE_DAY },

    // Regional settings and configuration
    { pattern: /^https?:\/\/.*\/v2\/regions\/.*$/, ttl: ONE_DAY },
    { pattern: /^https?:\/\/.*\/v2\/regions\/.*\/languages$/, ttl: ONE_DAY },
    { pattern: /^https:\/\/wmb-public-api-.*\.ridango.cloud\/v1\/region-languages$/, ttl: ONE_DAY },
    { pattern: /^https:\/\/wmb-public-api-.*\.ridango.cloud\/v1\/region-settings$/, ttl: ONE_DAY },

    // Terms and conditions
    { pattern: /^https?:\/\/.*\/v2\/.*\/terms-and-conditions\/channel\/web\/version$/, ttl: ONE_DAY },

    // ===== Static Reference Data (1 WEEK) =====
    // Routes, stops, patterns rarely change
    { pattern: /^https?:\/\/.*\/v1\/routes$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/.*\/v1\/routes\/.*$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/.*\/v1\/stops$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/.*\/v1\/stops\/.*$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/.*\/v1\/patterns$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/.*\/v1\/patterns\/.*$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/.*\/v1\/patterns\/.*\/stops$/, ttl: ONE_WEEK },

    // Translations and static assets
    { pattern: /^https?:\/\/.*\/v2\/.*\/translations\/get\/.*\.json$/, ttl: ONE_WEEK },
    { pattern: /^https?:\/\/wmb-public-api-.*\.ridango.cloud\/v1\/translations\/.*$/, ttl: ONE_WEEK },
    { pattern: /^assets\/settings\/locales\.json$/, ttl: ONE_WEEK },
    { pattern: /assets\/img\/footer\/explore\.svg$/, ttl: ONE_WEEK },
    { pattern: /assets\/img\/footer\/journey-planner\.svg$/, ttl: ONE_WEEK },
    { pattern: /assets\/img\/footer\/tickets\.svg$/, ttl: ONE_WEEK },
    { pattern: /assets\/img\/footer\/disruption-messages\.svg$/, ttl: ONE_WEEK },
    { pattern: /assets\/img\/ticket-details\/back\.svg$/, ttl: ONE_WEEK }
];

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
    constructor(
        private network: NetworkService,
        private cacheService: CacheService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if running on a web platform and skip cache logic
        if (!Capacitor.isNativePlatform()) {
            return next.handle(request);
        }

        const cacheKey = request.urlWithParams;

        // If offline, try to serve from cache
        if (this.network.getCurrentStatus() === false) {
            const cachedResponse = this.getCachedValue(cacheKey);
            if (cachedResponse) {
                return of(cachedResponse);
            }
        }

        return next.handle(request).pipe(
            tap((event) => this.cacheResponse(event, cacheKey)),
            catchError((error: HttpErrorResponse) => {
                return this.handleCacheError(error, cacheKey);
            })
        );
    }

    /**
     * Get cached value for a given cache key.
     * @param cacheKey - The URL with params
     * @returns HttpResponse with cached body or null
     */
    private getCachedValue(cacheKey: string): HttpResponse<any> | null {
        const encodedCacheKey = btoa(cacheKey);
        const cachedResponse = this.cacheService.getItem(encodedCacheKey);

        if (cachedResponse) {
            return new HttpResponse({
                body: cachedResponse,
                status: 200
            });
        }

        return null;
    }

    /**
     * Cache response if it matches any cache pattern.
     * @param event - HTTP event to cache
     * @param cacheKey - The URL with params
     */
    private cacheResponse(event: HttpEvent<any>, cacheKey: string): void {
        if (!(event instanceof HttpResponse) || this.network.getCurrentStatus() !== true) {
            return;
        }

        const matched = cachePatterns.find(({ pattern }) => pattern.test(cacheKey));
        if (matched) {
            const encodedCacheKey = btoa(cacheKey);
            this.cacheService.setItem(encodedCacheKey, event.body, matched.ttl);
        }
    }

    /**
     * Handle cache retrieval on error (offline scenarios).
     * @param error - HTTP error response
     * @param cacheKey - The URL with params
     * @returns Observable with cached response or throws error
     */
    private handleCacheError(error: HttpErrorResponse, cacheKey: string): Observable<HttpEvent<any>> {
        // Only handle offline errors (status 0)
        if (error.status !== 0) {
            throw error;
        }

        const cachedResponse = this.getCachedValue(cacheKey);
        if (cachedResponse) {
            return of(cachedResponse);
        }

        throw error;
    }
}
